// Der Hintergrund: Himmel, Nebel und zwei Sternebenen in **einem** Bildschritt.
//
// Die Zone ist ein **Ort** — ein konzentrischer Ring um die Kapsel. Ihre
// Darstellung war trotzdem ein globaler Zustand: Nebel, Himmelfarbe und
// Sternton kamen aus der Kameraposition und galten fuer den ganzen Bildschirm.
// Ueberflog man eine Grenze, faerbte sich das gesamte Bild um, samt der Gegend
// hinter einem, die noch zur alten Zone gehoerte — und Nebel und Sternton
// sprangen dabei hart, waehrend nur der Himmel weich ueberblendete.
//
// Hier faellt die Farbe je **Bildpunkt** aus seiner Weltposition. Damit liegt
// der Uebergang, wo er in der Welt liegt: als Ring durch die Szene. Bewegt sich
// nur die Kamera, aendert sich an einem Weltort nichts.
//
// Die Formel ist wortgleich die laufende Mischung aus `zonecolor.ts` — dieselbe
// Rechnung, einmal auf der CPU fuer den Rueckfall und einmal je Bildpunkt.
//
// Diese Datei importiert **kein Phaser**: sie ist Text und Mathematik, damit ein
// Test sie lesen kann.

import { ZONE_BLEND, ZONE_WINDOW } from './zonecolor';

/** Nur fuer den Kommentar in der GLSL-Quelle — die Breite steht in `zonecolor`. */
const ZONE_BLEND_HINT = ZONE_BLEND;

/**
 * Die laufende Mischung, **entrollt**.
 *
 * Als Schleife mit `uSky[i]` geschrieben war der Hintergrund gemessen dreimal
 * so teuer: ein CPU-Rasterizer kann ein Uniform-Feld nicht mit einem
 * veraenderlichen Index adressieren und faellt auf Speicherzugriffe zurueck.
 * Mit festen Indizes ist es ein paar Registeroperationen.
 *
 * Erzeugt statt getippt: die Zahl der Stufen steht in `ZONE_WINDOW`, und eine
 * von Hand ausgeschriebene Kopie liefe beim naechsten Aendern still auseinander.
 */
const ZONE_MIX = Array.from({ length: ZONE_WINDOW - 1 }, (_, k) => {
  const i = k + 1;
  return `    t = clamp((d - (uFrom[${i}] - uBlend)) / uBlend, 0.0, 1.0);\n`
    + `    sky  = mix(sky,  uSky[${i}],  t);\n`
    + `    star = mix(star, uStar[${i}], t);\n`
    + `    neb  = mix(neb,  uNeb[${i}],  t);`;
}).join('\n');

/** Name des Shaderprogramms. */
export const BACKDROP_SHADER_NAME = 'backdrop';

/**
 * Die Uniforms, die der Shader kennt — und die die Szene setzen muss.
 *
 * Ein Uniform, das die GLSL-Quelle **nicht** deklariert, wird still ignoriert:
 * der Hintergrund stuende dann in der falschen Farbe da, ohne Fehlermeldung.
 * Gate FG-40 haelt diese Liste gegen die Quelle und gegen die Szene.
 */
export const BACKDROP_UNIFORMS = [
  'uNebula',
  'uStars0',
  'uStars1',
  'uWorld0',
  'uWorldSize',
  'uNebulaUV',
  'uStars0UV',
  'uStars1UV',
  'uBlend',
  'uFrom',
  'uSky',
  'uStar',
  'uNeb',
] as const;

/**
 * Deckkraft des Nebels — die Zahl, die vorher als `setAlpha(0.9)` am
 * Kachelbild stand.
 */
export const NEBULA_ALPHA = 0.9;

/**
 * Der Fragment-Shader.
 *
 * `uWorld0` und `uWorldSize` spannen den Quad in **Weltmetern** auf; daraus
 * faellt je Bildpunkt seine Weltposition und damit sein Abstand zur Kapsel.
 *
 * Die drei `…UV`-Uniforms sind die affine Abbildung Weltposition → Texel je
 * Ebene: `texel = welt * uv.x + uv.yz`. Der Parallaxfaktor steckt darin, denn
 * er ist auf der CPU eine Multiplikation und im Shader sonst drei Uniforms
 * mehr.
 */
export const BACKDROP_FRAGMENT = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

varying vec2 outTexCoord;

uniform sampler2D uNebula;
uniform sampler2D uStars0;
uniform sampler2D uStars1;

uniform vec2 uWorld0;
uniform vec2 uWorldSize;
uniform vec3 uNebulaUV;
uniform vec3 uStars0UV;
uniform vec3 uStars1UV;

uniform float uBlend;
uniform float uFrom[${ZONE_WINDOW}];
uniform vec3 uSky[${ZONE_WINDOW}];
uniform vec3 uStar[${ZONE_WINDOW}];
uniform vec3 uNeb[${ZONE_WINDOW}];

void main ()
{
    vec2 welt = uWorld0 + outTexCoord * uWorldSize;
    float d = length(welt);

    // Laufende Mischung ueber das Zonenfenster — wortgleich die Rechnung aus
    // zonecolor.ts. Vor einer Grenze ist der Anteil 0, dahinter 1; im
    // Uebergangsband dazwischen.
    //
    // Die **beiden** Himmelfarben werden auf der CPU gemittelt, nicht hier:
    // ihr Mittel ist das Einzige, was der Hintergrund je gebraucht hat, und
    // eine Mischung je Bildpunkt weniger ist auf einem schwachen Geraet
    // messbar.
    vec3 sky  = uSky[0];
    vec3 star = uStar[0];
    vec3 neb  = uNeb[0];
    float t;
${ZONE_MIX}

    // Nebel und Sterne sind **Struktur**, nicht Farbe: weisse Kacheln mit
    // Alpha. Getoent wird hier, also weich — und nicht mehr als Texturwechsel.
    vec2 uvN = welt * uNebulaUV.x + uNebulaUV.yz;
    vec2 uv0 = welt * uStars0UV.x + uStars0UV.yz;
    vec2 uv1 = welt * uStars1UV.x + uStars1UV.yz;

    vec3 farbe = mix(sky,   neb,  texture2D(uNebula, uvN).a * ${NEBULA_ALPHA});
    farbe      = mix(farbe, star, texture2D(uStars0, uv0).a);
    farbe      = mix(farbe, star, texture2D(uStars1, uv1).a);

    // Ein halbes Bit Streuung gegen Baender. Der Uebergang laeuft ueber
    // ${ZONE_BLEND_HINT} Meter, die Himmelfarben trennen keine 60 Stufen — das sind rund
    // neun Bildpunkte je Stufe und damit sichtbare Ringe. Weniger als eine
    // Stufe Rauschen loest sie auf und ist selbst nicht zu sehen.
    //
    // Gestreut wird ueber eine **R2-Folge**, nicht ueber das uebliche
    // fract(sin(...)): ein Sinus je Bildpunkt kostete auf dem CPU-Rasterizer
    // mehr als der ganze uebrige Hintergrund. Das ist keine
    // Kanteninterpolation — die Streuung liegt unterhalb der Quantisierung,
    // der Pixel-Look bleibt.
    float dither = fract(dot(gl_FragCoord.xy, vec2(0.7548776662, 0.5698402909))) - 0.5;
    gl_FragColor = vec4(farbe + dither / 255.0, 1.0);
}
`;

/**
 * Die affine Abbildung Weltposition → Texel einer Parallaxebene.
 *
 * Ein bildschirmfestes Kachelbild sampelt an `(bildpunkt + kamera · f) / kachel`
 * — und der Bildpunkt eines Weltorts ist `(welt − kamera) · zoom`. Beides
 * zusammengefasst ergibt genau diese Gerade. Sie steht hier und nicht im
 * Shader, weil sie je Bild **einmal** gilt statt je Bildpunkt.
 *
 * Geschrieben wird in ein uebergebenes Ziel (Gate FG-14).
 */
export function layerUV(
  camX: number, camY: number, zoom: number, factor: number, tile: number,
  out: [number, number, number],
): [number, number, number] {
  out[0] = zoom / tile;
  out[1] = (camX * (factor - zoom)) / tile;
  out[2] = (camY * (factor - zoom)) / tile;
  return out;
}
